import { botBudgetDay } from "./bot-budget.js";
import { zonedDateTime } from "./cron.js";

/**
 * Open work (the scratchpad) as follow-through: which items are late or close, in what order to
 * show them, when a bot should check one before it is due, and when Chief's morning review is due.
 * One source for the server, the Details panel and the check-in.
 */

/** An item due within this window is at risk. */
export const OPEN_WORK_DUE_SOON_MS = 24 * 60 * 60_000;

export type OpenWorkState = "overdue" | "due_soon" | "upcoming" | "undated";

function time(value: Date | string | null | undefined): number | null {
  if (value == null) return null;
  const ms = typeof value === "string" ? Date.parse(value) : value.getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function openWorkState(dueAt: Date | string | null | undefined, now: Date): OpenWorkState {
  const due = time(dueAt);
  if (due === null) return "undated";
  if (due <= now.getTime()) return "overdue";
  return due - now.getTime() <= OPEN_WORK_DUE_SOON_MS ? "due_soon" : "upcoming";
}

/** Late first, then soonest due; undated work last, most recently touched first. */
export function sortOpenWork<T extends { dueAt?: Date | string | null; updatedAt: Date | string }>(
  items: readonly T[],
): T[] {
  return [...items].sort((left, right) => {
    const a = time(left.dueAt);
    const b = time(right.dueAt);
    if (a !== null && b !== null) return a - b;
    if (a !== null) return -1;
    if (b !== null) return 1;
    return (time(right.updatedAt) ?? 0) - (time(left.updatedAt) ?? 0);
  });
}

const MIN_LEAD_MS = 15 * 60_000;
const MAX_LEAD_MS = 24 * 60 * 60_000;
/** A check closer than this to now is not worth a wakeup; the bot is working on it already. */
const MIN_CHECK_DELAY_MS = 60_000;

/**
 * When to check an item before it is due: a quarter of the time left, at least 15 minutes and at
 * most a day ahead. Null when it is due too soon (or already late) for a check to help.
 */
export function openWorkCheckAt(dueAt: Date, now: Date): Date | null {
  const left = dueAt.getTime() - now.getTime();
  const lead = Math.min(MAX_LEAD_MS, Math.max(MIN_LEAD_MS, left / 4));
  const at = dueAt.getTime() - lead;
  if (at - now.getTime() < MIN_CHECK_DELAY_MS) return null;
  return new Date(at);
}

/** Chief's morning review runs on the first check-in at or after this local hour. */
export const OPEN_WORK_MORNING_HOUR = 8;

/** Today's review time in the owner's timezone, when a check-in at `now` should hold it. */
export function morningReviewDue(
  lastRunAt: Date | null,
  now: Date,
  timeZone: string | null | undefined,
): boolean {
  const day = botBudgetDay(now, timeZone);
  const hour = String(OPEN_WORK_MORNING_HOUR).padStart(2, "0");
  const reviewAt = zonedDateTime(`${day.key}T${hour}:00:00`, day.timeZone);
  if (!reviewAt || now < reviewAt) return false;
  return !lastRunAt || lastRunAt < reviewAt;
}
